import { animate, state, style, transition, trigger } from '@angular/animations';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';

const DEFAULT_EXPAND_COLLAPSE_NAME = 'expandCollapse';
const DEFAULT_DURATION_MS = 200;

/**
 * Config interface for the expand-collapse animation.
 *
 * @property name the name for the animation
 * @property durationMS the duration of the animation in milliseconds
 * @property delayEnterMS the enter delay for the animation in milliseconds
 * @property delayLeaveMS the leave delay for the animation in milliseconds
 * @property fadeIn boolean if the element should fade in with opacity
 * @property animateHeight boolean if the height should be animated
 * @property animateMargins boolean if the margins should be animated
 * @property animatePadding boolean if the padding should be animated
 */
export interface ExpandCollapseAnimationConfig {
	name?: string;
	collapsedStateName?: string;
	expandedStateName?: string;
	durationMS?: number;
	delayEnterMS?: number;
	delayLeaveMS?: number;
	fadeIn?: boolean;
	animateHeight?: boolean;
	animateMargins?: boolean;
	animatePadding?: boolean;
	applyOverflow?: boolean;
}

/**
 * Establish the default values for the expand-collapse animation.
 *
 * @param config the config to use to determine what default values need to be set
 */
const establishDefaultValues = (config?: ExpandCollapseAnimationConfig): ExpandCollapseAnimationConfig => ({
	name: config?.name ?? DEFAULT_EXPAND_COLLAPSE_NAME,
	collapsedStateName: config?.collapsedStateName ?? 'void',
	expandedStateName: config?.expandedStateName ?? '*',
	durationMS: config?.durationMS ?? DEFAULT_DURATION_MS,
	delayEnterMS: config?.delayEnterMS ?? 0,
	delayLeaveMS: config?.delayLeaveMS ?? 0,
	fadeIn: config?.fadeIn ?? true,
	animateHeight: config?.animateHeight ?? true,
	animateMargins: config?.animateMargins ?? true,
	animatePadding: config?.animatePadding ?? true,
	applyOverflow: config?.applyOverflow ?? true
});

/**
 * Animation for adding an expand-collapse with a configuration parameter object.
 *
 * @param config the config options for the animation
 */
export const expandCollapseAnimation = (config?: ExpandCollapseAnimationConfig) => {
	const updatedConfig = establishDefaultValues(config);
	return trigger(updatedConfig.name!, [
		state(
			updatedConfig.collapsedStateName!,
			style(
				pickBy(
					{
						opacity: updatedConfig.fadeIn ? 0 : 1,
						height: updatedConfig.animateHeight ? 0 : '*',
						marginTop: updatedConfig.animateMargins ? 0 : '*',
						marginBottom: updatedConfig.animateMargins ? 0 : '*',
						paddingTop: updatedConfig.animatePadding ? 0 : '*',
						paddingBottom: updatedConfig.animatePadding ? 0 : '*',
						overflow: updatedConfig.applyOverflow ? 'hidden' : undefined!
					},
					obj => !isNil(obj)
				)
			)
		),
		state(
			updatedConfig.expandedStateName!,
			style(
				pickBy(
					{
						opacity: 1,
						height: '*',
						marginTop: '*',
						marginBottom: '*',
						paddingTop: '*',
						paddingBottom: '*',
						overflow: updatedConfig.applyOverflow ? 'visible' : undefined!
					},
					obj => !isNil(obj)
				)
			)
		),
		transition(
			`${updatedConfig.collapsedStateName} => ${updatedConfig.expandedStateName}`,
			animate(`${updatedConfig.durationMS}ms {{delayEnter}}ms ease-out`),
			{
				params: { delayEnter: updatedConfig.delayEnterMS }
			}
		),
		transition(
			`${updatedConfig.expandedStateName} => ${updatedConfig.collapsedStateName}`,
			animate(`${updatedConfig.durationMS}ms {{delayLeave}}ms ease-out`),
			{
				params: { delayLeave: updatedConfig.delayLeaveMS }
			}
		)
	]);
};
